import { useCallback } from 'react';

import { getConsentsData } from 'helpers/candidate_application/consent-data-helpers';
import { buildAllFormValues } from 'helpers/candidate_application/form-page-helpers';
import useFormSubmission from 'hooks/use-form-submission';
import usePageVisibilityRedirect from 'hooks/use-page-visibility-redirect';
import useResetSubsequentSections from 'hooks/use-reset-subsequent-sections';
import useVisibleSubsections from 'hooks/use-visible-subsections';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CustomToast } from 'components/Toast/CustomToast';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import FormPageRenderer from '../../FormPages/FormPageRenderer';

const FCRA = ({
  candidate,
  brand,
  handlers,
  token,
  sectionKey,
  pageIndex,
  section,
  subsections,
  hasIndexPage,
  isSinglePage,
  fetched,
  check_values,
}) => {
  const { t: translate } = useTranslation();
  const { candidateFields } = useSelector(selectCandidateApplication);
  const visibleSubsections = useVisibleSubsections(subsections);
  const { saveFormValuesAndSubmit } = useFormSubmission();
  const { buildSystemValuesWithReset } = useResetSubsequentSections(sectionKey, check_values);

  // Calculate total pages using filtered subsections
  const totalPages = (visibleSubsections || [])?.length || 0;
  const currentPage = parseInt(pageIndex) || 0;

  // Redirect if current page is out of bounds after visibility changes
  usePageVisibilityRedirect({
    currentPage,
    totalPages,
    visibleSubsections,
    preSubsectionFlows: [],
    hasIndexPage: false,
    sectionKey,
    token,
  });

  const handleFormSubmit = useCallback(
    async (formData, context, temp_Save = false) => {
      try {
        const hasValidationIssues = context?.hasValidationIssues || false;
        const currentPagePrefixes = context?.currentPagePrefixes || null;

        let status = 'in-progress'; // Initially empty
        if (currentPage === totalPages - 1 && !hasValidationIssues) {
          status = 'completed';
        } else if (hasValidationIssues) {
          status = 'in-progress';
        }

        // Build system values with current section status and reset logic
        const systemValues = buildSystemValuesWithReset('fcra_status', status);

        // Collect consents data - for single page, collect all subsections; for multi-page, only current
        // This ensures we don't overwrite signatures from other pages with undefined values
        const currentSubsection = isSinglePage ? null : visibleSubsections[currentPage];
        const consentsData = getConsentsData(
          formData,
          sectionKey,
          currentSubsection ? [currentSubsection] : visibleSubsections,
          {
            allValues: buildAllFormValues({
              candidateFields,
              watchValues: formData,
              currentPagePrefixes: context?.currentPagePrefixes,
            }),
            fieldPathIndex: candidateFields?.fieldPathIndex ?? {},
          },
        );

        // Save the data and update state
        await saveFormValuesAndSubmit(
          sectionKey,
          formData,
          token,
          systemValues,
          currentPagePrefixes,
          false,
          consentsData,
        );

        if (currentPage === totalPages - 1) {
          toast.custom(
            t => (
              <CustomToast
                t={t}
                title={translate('candidate.sections.fcraDetailsAreSafe')}
                message={translate('candidate.sections.weveSavedYourFcraInformation')}
                type="success"
              />
            ),
            {
              duration: 5000,
            },
          );
        }
        return true;
      } catch (error) {
        toast.custom(
          t => (
            <CustomToast
              t={t}
              title={translate('candidate.sections.failedToSaveFcraDetails')}
              message={error?.message || error || translate('candidate.sections.pleaseTryAgain')}
              type="error"
            />
          ),
          {
            duration: 5000,
          },
        );
        return false;
      }
    },
    [
      token,
      currentPage,
      totalPages,
      saveFormValuesAndSubmit,
      sectionKey,
      buildSystemValuesWithReset,
      visibleSubsections,
      isSinglePage,
      candidateFields,
    ],
  );

  return (
    <FormPageRenderer
      subsections={visibleSubsections}
      candidate={candidate}
      brand={brand}
      handlers={handlers}
      fetched={fetched}
      singlePage={isSinglePage}
      sectionKey={sectionKey}
      pageIndex={isSinglePage ? undefined : currentPage}
      globalPageIndex={currentPage}
      totalPages={totalPages}
      primaryActionLabel={translate('common.continue')}
      onFormSubmit={handleFormSubmit}
    />
  );
};

export default FCRA;
